import React, { useState, useEffect } from 'react';
import { useAlert } from 'react-alert';
import { useTranslation } from 'react-i18next';

import { Button, Checkbox, Modal } from '~/components';
import { useMe } from '~/services';
import { useApi } from '~/hooks';

import { MainForm, ModalView } from './style';

/**
 * Componente de configuração de polos para processo judicial
 * Permite configurar quais tipos de alertas de risco devem ser habilitados
 */
const ProcessPolaritySettings = () => {
  const { t } = useTranslation('settings');
  const { user, group, newGroup } = useMe();
  const [isSubmitting, setSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const alert = useAlert();
  const api = useApi();

  // Estados para as polos (default: todas marcadas)
  const [polarities, setPolarities] = useState({
    active: true,   // Réu
    passive: true,  // Autor
    neutral: true   // Outros
  });

  // Configurar grupo padrão se não existir
  useEffect(() => {
    if (Object.entries(group).length === 0) {
      newGroup({
        value: user.hierarquia[0].id,
        label: user.hierarquia[0].cliente,
      });
    }
  }, [group, newGroup, user.hierarquia]);

  /**
   * Carrega as configurações existentes da API
   */
  const loadSettings = async () => {
    try {
      const response = await api().get('/process-polarities', {
        params: {
          client_id: group.value === 0 ? null : group.value,
          group_id:  group.value === 0 ? user.grupos[0].idgrupo : null
        }
      });
      if (response?.data) {
        setPolarities({
          id:  response?.data?.id || null,
          active:  response?.data?.active  === 1 ? true : false,
          passive: response?.data?.passive === 1 ? true : false,
          neutral: response?.data?.neutral === 1 ? true : false
        });
      }
    } catch (error) {
      // Se não existir configuração, mantém o default (todas marcadas)
      console.log('Nenhuma configuração encontrada, usando padrão');
      console.log(error);
    }
  };

  /**
   * Salva as configurações na API
   */
  const handleSave = async () => {
    // Validação: pelo menos uma opção deve estar selecionada
    if (!polarities.active && !polarities.passive && !polarities.neutral) {
      alert.error(t('process_polarity.validation.minimum_one'));
      return;
    }

    setSubmitting(true);
    const payload = {
      id: polarities.id,
      client_id: group.value === 0 ? null : group.value,
      group_id: group.value === 0 ? user.grupos[0].idgrupo : null,
      active: polarities.active,
      passive: polarities.passive,
      neutral: polarities.neutral
    };

    try {
      if (polarities.id) {
        await api().put(`/process-polarities`, payload);
      } else {
        await api().post('/process-polarities', payload);
      }
      alert.success(t('process_polarity.success'));
     
      setHasChanges(false);
      setShowModal(false);
    } catch (error) {
      alert.error(t('process_polarity.error'));
    } finally {
      setSubmitting(false);
      resetPolarities();
    }
  };

  const resetPolarities = () => {
    setPolarities({
      active: true,
      passive: true,
      neutral: true
    });
  };

  /**
   * Atualiza o estado dos polos e marca que houve mudanças
   */
  const handlePolarityChange = (key, value) => {
    setPolarities(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };

  /**
   * Carrega configurações quando o modal é aberto
   */
  const handleOpenModal = () => {
    loadSettings();
    setShowModal(true);
    setHasChanges(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setHasChanges(false);
    setSubmitting(false);
    resetPolarities();
  };

  return (
    <MainForm>
      <div>
        <p>{t('process_polarity.info_text')}</p>
        <Button
          label={t('process_polarity.button')}
          onClick={handleOpenModal}
        />
      </div>

      <Modal
        show={showModal}
        onShow={handleCloseModal}
        title={t('process_polarity.modal.title')}
        hasActions
        action={{
          label: t('process_polarity.modal.save_button'),
          onClick: handleSave,
          loading: isSubmitting,
          disabled: !hasChanges
        }}
      >
        <ModalView>
          <p>{t('process_polarity.modal.description')}</p>

          <Checkbox
            id="reu"
            label={t('process_polarity.reu')}
            onChange={(e) => handlePolarityChange('passive', e.target.checked)}
            defaultChecked={polarities.passive}
          />

          <Checkbox
            id="autor"
            label={t('process_polarity.autor')}
            onChange={(e) => handlePolarityChange('active', e.target.checked)}
            defaultChecked={polarities.active}
          />

          <Checkbox
            id="outros"
            label={t('process_polarity.outros')}
            onChange={(e) => handlePolarityChange('neutral', e.target.checked)}
            defaultChecked={polarities.neutral}
          />
        </ModalView>
      </Modal>
    </MainForm>
  );
};

export default ProcessPolaritySettings;
